import React, { useState, useEffect } from 'react';
import { Menu, X, Share2 } from 'lucide-react';
import { Button } from '../ui/button';
import { propertyData, brandAssets } from '../../data/mock';

const Header = ({ onInquire, onShare, isVideoPlaying }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  // New navigation structure per requirements
  const navLinks = [
    { id: 'overview', label: 'Overview' },
    { id: 'estate', label: 'The Estate' },
    { id: 'infrastructure', label: 'Infrastructure' },
    { id: 'market-evidence', label: 'Market Evidence' },
    { id: 'presentation', label: 'Presentation' },
    { id: 'virtual-tour', label: 'Virtual Tour' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'inquire', label: 'Inquire' },
  ];

  const showCondensedHeadline = isVideoPlaying && !isScrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showCondensedHeadline
          ? 'bg-[#0d0d0d]/95 backdrop-blur-lg shadow-xl'
          : isScrolled
          ? 'bg-[#0d0d0d]/95 backdrop-blur-lg shadow-2xl py-2'
          : 'bg-gradient-to-b from-black/80 to-transparent py-3'
      }`}
    >
      {/* Condensed Headline Bar */}
      {showCondensedHeadline && (
        <div className="bg-gradient-to-r from-amber-600/10 via-amber-500/5 to-amber-600/10 border-b border-amber-500/20 py-2 px-4 animate-in slide-in-from-top duration-300">
          <p className="text-center text-white/90 text-xs sm:text-sm font-light tracking-wide max-w-5xl mx-auto leading-tight">
            <span className="text-amber-400 font-medium">{propertyData.brandName}</span>
            <span className="hidden sm:inline"> — Private 20-Acre Central Florida Legacy Property</span>
            <span className="hidden lg:inline"> • {propertyData.priceShort}</span>
          </p>
        </div>
      )}

      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${showCondensedHeadline ? 'py-2' : ''}`}>
        <div className="flex items-center justify-between">
          {/* W Crest Logo */}
          <div className="flex items-center space-x-3">
            <img 
              src={brandAssets.wCrest} 
              alt="William Lane Estate" 
              className={`transition-all duration-300 ${showCondensedHeadline ? 'h-10 w-10' : 'h-12 w-12'} object-contain`}
            />
            <div className="hidden sm:block">
              <p className={`text-white font-serif tracking-wide ${showCondensedHeadline ? 'text-xs' : 'text-sm'}`}>
                {propertyData.brandName}
              </p>
              {!showCondensedHeadline && (
                <p className="text-amber-400/80 text-xs">{propertyData.brandTagline}</p>
              )}
            </div>
          </div>

          {/* Desktop Navigation */}
          {!showCondensedHeadline && (
            <nav className="hidden xl:flex items-center space-x-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => link.id === 'inquire' ? onInquire() : scrollToSection(link.id)}
                  className={`px-3 py-2 text-sm transition-colors duration-300 font-light tracking-wide ${
                    link.id === 'inquire' 
                      ? 'text-amber-400 hover:text-amber-300' 
                      : 'text-white/80 hover:text-amber-400'
                  }`}
                  data-testid={`nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>
          )}

          {/* CTA Buttons - NO REALTOR CONTACT */}
          <div className={`hidden md:flex items-center space-x-3 transition-all duration-300 ${showCondensedHeadline ? 'scale-95' : ''}`}>
            {!showCondensedHeadline && (
              <Button
                onClick={onShare}
                variant="outline"
                size="sm"
                className="border-amber-400/50 text-amber-400 hover:bg-amber-400/10 hover:border-amber-400"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            )}
            <Button
              onClick={onInquire}
              size="sm"
              className={`bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg shadow-amber-500/30 ${showCondensedHeadline ? 'py-1.5 px-4 text-xs' : ''}`}
              data-testid="inquire-privately-btn"
            >
              {showCondensedHeadline ? 'Inquire' : 'Inquire Privately'}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="xl:hidden p-2 text-white hover:text-amber-400 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && !showCondensedHeadline && (
          <div className="xl:hidden mt-4 pb-4 border-t border-white/10 pt-4 animate-in slide-in-from-top duration-300 bg-[#0d0d0d]/98 backdrop-blur-xl -mx-4 px-4 rounded-b-2xl">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => link.id === 'inquire' ? onInquire() : scrollToSection(link.id)}
                  className="px-4 py-3 text-left text-white/90 hover:text-amber-400 hover:bg-white/5 rounded-lg transition-all duration-300 font-medium"
                >
                  {link.label}
                </button>
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-white/10">
              <Button
                onClick={onInquire}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                data-testid="mobile-inquire-btn"
              >
                Request Private Estate Information
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Persistent Inquire Button (appears after scrolling) */}
      {isScrolled && !isMobileMenuOpen && (
        <div className="fixed bottom-4 right-4 md:hidden z-50">
          <Button
            onClick={onInquire}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-xl shadow-amber-500/30 rounded-full px-6 py-3"
            data-testid="floating-inquire-btn"
          >
            Inquire Privately
          </Button>
        </div>
      )}
    </header>
  );
};

export default Header;
